<script lang="ts" setup>
import { type IconName, StackedIcon } from '@/fontawesome';
import { useAnimate, type MaybeElement, type UseAnimateOptions } from '@vueuse/core';
import { onMounted, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
    icon: IconName | StackedIcon;
    family?: string;
    animate?: 'shake';

    /**
     * Rotate the icon
     */
    rotate?: number;
}>();

const iconelement = useTemplateRef<MaybeElement>('iconelement');

const iconvalue = ref<IconName>('');
const familyvalue = ref<string | undefined>(props.family);

const parseIconFamily = (icon: IconName | StackedIcon) => {
    if (typeof icon === 'string') {
        if (icon.includes(' ')) {
            const [_family, _icon] = icon.split(' ');
            iconvalue.value = _icon;
            familyvalue.value = _family;
        } else {
            iconvalue.value = icon;
            familyvalue.value = props.family;
        }
    }
};

// TODO: Move these animations to a composable?
const shakeKeyframes = [
    { transform: 'rotate(0deg)', offset: 0 },
    { transform: 'rotate(15deg)', offset: 0.1 },
    { transform: 'rotate(0deg)', offset: 0.15 },
    { transform: 'rotate(-15deg)', offset: 0.2 },
    { transform: 'rotate(0deg)', offset: 0.25 },
    { transform: 'rotate(0deg)', offset: 1 },
];

const shakeOptions: UseAnimateOptions = {
    duration: 1500,
    iterations: 5,
    direction: 'normal',
};

parseIconFamily(props.icon);

watch(
    () => props.icon,
    (newVal) => parseIconFamily(newVal),
);

onMounted(() => {
    if (props.animate) {
        const { play } = useAnimate(iconelement, props.animate === 'shake' ? shakeKeyframes : [], shakeOptions);
        play();
    }
});
</script>

<template>
    <span class="eq-icon">
        <span
            v-if="iconvalue.startsWith('fi-')"
            :class="{ fi: true, [iconvalue]: true }"
        ></span>
        <font-awesome-icon
            v-if="!iconvalue.startsWith('fi-') && iconvalue != ''"
            :icon="(familyvalue ?? 'fa-light') + ' ' + iconvalue"
            v-bind="$attrs"
            ref="iconelement"
            :style="{ transform: props.rotate ? `rotate(${props.rotate}deg)` : 'undefined' }"
        />
        <span
            v-if="icon instanceof StackedIcon"
            class="fa-stack"
            v-bind="$attrs"
        >
            <font-awesome-icon
                v-if="icon.iconName !== ''"
                :icon="(familyvalue ?? 'fa-light') + ' ' + icon.iconName"
                ref="iconelement"
                class="fa-stack-1x"
                :class="{ 'fa-stack-1x': icon!.stackedIcons!.length > 0 }"
                :style="{ transform: icon.rotate ? `rotate(${icon.rotate}deg)` : 'undefined' }"
            />
            <font-awesome-icon
                v-for="i in icon!.stackedIcons"
                :key="i.iconName"
                :icon="(familyvalue ?? 'fa-light') + ' ' + i.iconName"
                :transform="i.transform?.toFontAwesomeStackString()"
                :style="{ color: i.transform?.color ?? 'inherit' }"
                class="fa-stack-1x"
            />
        </span>
    </span>
</template>
